import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
} from 'react-router-dom';
import './index.css';
import App from './App.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HomePageProvider } from './components/homepage/HomePageContext.tsx';
import 'leaflet/dist/leaflet.css';
import 'react-phone-input-2/lib/style.css'; // Import the library's base styles first
import '../styles/phone-input.css'; // Then import your custom styles
import '@/styles/custom-scrollbar.css'; // Import custom scrollbar styles

// 🔇 SUPPRESS: React Router v7 transition warning until v7_startTransition flag is available
const originalWarn = console.warn;
console.warn = (...args) => {
  // Suppress the specific React Router v7 transition warning
  if (args[0]?.includes?.('React Router will begin wrapping state updates in `React.startTransition` in v7')) {
    return; // Skip this specific warning
  }
  originalWarn.apply(console, args);
};

// Global request tracker to prevent too many requests
const requestRegistry = new Map<string, { timestamp: number, count: number }>();

// Create a client for react-query with custom retry logic
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      refetchOnWindowFocus: false, // Disable automatic refetching on window focus
      retry: (failureCount, error: any) => {
        // Don't retry on 429 (Too Many Requests) errors
        if (error?.response?.status === 429) {
          console.warn('Rate limit exceeded, not retrying');
          return false;
        }
        
        // Only retry up to 2 times for other errors
        return failureCount < 2;
      },
      retryDelay: (attemptIndex) => {
        // Implement exponential backoff: 1s, 2s, 4s, etc.
        return Math.min(1000 * (2 ** attemptIndex), 30000);
      }
    },
    mutations: {
      retry: false // Don't retry mutations by default
    }
  }
});

// Configure the router with future flags
const router = createBrowserRouter(
  [
    {
      path: "*",
      element: <App />,
      errorElement: <ErrorBoundary />
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
      // NOTE: v7_startTransition is not yet available in React Router v6.30.0
      // This flag will be available in a future version closer to v7 release
    },
  }
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <HomePageProvider>
        <RouterProvider router={router} />
      </HomePageProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);